/**
 * Image Art Direction Utilities
 *
 * Transforms srcsetCandidates for RespImage component with optional art direction support.
 */

import type {
  SrcsetCandidate,
  ArtDirectionVariant,
} from '@/app/_components/core/resp-image';

/**
 * 1x1 transparent PNG as base64 data URL
 * Used as a fallback poster for videos when no poster image is provided
 */
export const TRANSPARENT_PIXEL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

/**
 * Art direction variant input - maps a media query to srcset candidates
 */
export interface ArtDirectionVariantInput {
  media: string;
  srcsetCandidates: SrcsetCandidate[];
  sizes?: string;
}

/**
 * Options for transformSrcsetCandidates with art direction
 */
export interface TransformWithArtDirectionOptions {
  fallback: SrcsetCandidate[];
  variants?: ArtDirectionVariantInput[];
}

/**
 * Return type for transformSrcsetCandidates
 */
export interface TransformSrcsetCandidatesResult {
  srcsetCandidates: SrcsetCandidate[];
  artDirection?: ArtDirectionVariant[];
}

/**
 * Transforms srcsetCandidates for RespImage component
 *
 * Supports three call signatures:
 * 1. Simple array (no art direction) - just pass through
 * 2. Record format (no art direction) - uses first format as fallback
 * 3. Object with fallback and optional variants (explicit art direction control)
 *
 * @example
 * // Simple array - no art direction
 * transformSrcsetCandidates([
 *   { imageUrl: '/img-400.jpg', width: 400, height: 400 },
 * ])
 * // => { srcsetCandidates: [...], artDirection: undefined }
 *
 * @example
 * // Record format - no art direction, uses first format
 * transformSrcsetCandidates({
 *   square: [{ imageUrl: '/square.jpg', width: 400, height: 400 }],
 * })
 * // => { srcsetCandidates: [...square], artDirection: undefined }
 *
 * @example
 * // With explicit art direction control
 * const image = { landscape: [...], portrait: [...] }; // from backend
 * transformSrcsetCandidates({
 *   fallback: image.portrait,
 *   variants: [
 *     { media: '(orientation: landscape)', srcsetCandidates: image.landscape },
 *   ]
 * })
 * // => { srcsetCandidates: [...portrait], artDirection: [{ media: '...', srcsetCandidates: [...landscape] }] }
 */
export function transformSrcsetCandidates(
  input:
    | SrcsetCandidate[]
    // Partial: the art-direction record only carries SOME orientation keys
    // (mirrors srcsetCandidatesSchema — a plain Record would claim all keys).
    | Partial<Record<string, SrcsetCandidate[]>>
    | TransformWithArtDirectionOptions
): TransformSrcsetCandidatesResult {
  // Simple array - no art direction
  if (Array.isArray(input)) {
    return {
      srcsetCandidates: input,
      artDirection: undefined,
    };
  }

  // Check if it's TransformWithArtDirectionOptions (has 'fallback' property)
  if ('fallback' in input) {
    const options = input as TransformWithArtDirectionOptions;
    const { fallback, variants } = options;

    // No variants - just return fallback
    if (!variants || variants.length === 0) {
      return {
        srcsetCandidates: fallback,
        artDirection: undefined,
      };
    }

    // Build art direction from variants
    const artDirection: ArtDirectionVariant[] = variants.map((variant) => ({
      media: variant.media,
      srcsetCandidates: variant.srcsetCandidates,
      sizes: variant.sizes,
    }));

    return {
      srcsetCandidates: fallback,
      artDirection,
    };
  }

  // Record format - flatten all formats into single array, no art direction
  // (flatMap with ?? []: partial record values may be undefined per type)
  return {
    srcsetCandidates: Object.values(input).flatMap((arr) => arr ?? []),
    artDirection: undefined,
  };
}
